<div class="modal-container">
  <div class="modal-header">
    <h2>Add New Ticket</h2>
    <button class="close-btn" (click)="onCancel()">×</button>
  </div>

  <div class="modal-body">
    <div class="warning-box">
      Do not enter any PHI unless you first select the "Attachment Contains PHI" checkbox below and enter it into the "PHI description" field. Any supporting documents or other information that may contain PHI should be entered <strong>ONLY IF THE "Attachment Contains PHI" checkbox is checked.</strong> Please <a href="https://www.hhs.gov/hipaa/for-professionals/special-topics/de-identification/index.html" target="_blank">Click Here</a> to view PHI identifiers. We are not liable for any PHI sent in error.
    </div>

    <form [formGroup]="ticketForm" (ngSubmit)="createTicket()">
      <div class="form-field">
        <label>Full Name <span class="required">*</span></label>
        <input type="text" formControlName="fullName" readonly>
      </div>

      <div class="form-field">
        <label>Email Address <span class="required">*</span></label>
        <input type="email" formControlName="emailAddress" readonly>
      </div>

      <div class="form-field">
        <label>Ticket Category <span class="required">*</span></label>
        <mat-form-field appearance="outline" class="measure-select-field" subscriptSizing="dynamic">
          <mat-select formControlName="category" placeholder="Select Category">
            <mat-option *ngFor="let category of categories" [value]="category.name">
              {{ category.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="form-field" *ngIf="measureFieldMode === 'multi'">
        <label>Impacted Measure(s) <span class="required">*</span></label>
        <mat-form-field appearance="outline" class="measure-select-field" subscriptSizing="dynamic">
          <mat-select
            formControlName="impactedMeasureCodes"
            multiple
            placeholder="Measure Selection">
            <mat-option>
              <ngx-mat-select-search
                [formControl]="measureSearchControl"
                placeholderLabel="Search measures..."
                noEntriesFoundLabel="No measures found">
              </ngx-mat-select-search>
            </mat-option>
            <mat-option *ngFor="let option of filteredMeasureOptions" [value]="option.code">
              {{ option.title }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <div class="measure-chip-list" *ngIf="ticketForm.get('impactedMeasureCodes')?.value?.length">
          <span class="measure-chip" *ngFor="let code of ticketForm.get('impactedMeasureCodes')?.value">
            {{ getMeasureTitle(code) }}
            <button type="button" class="measure-chip-remove" (click)="removeImpactedMeasure(code)">×</button>
          </span>
        </div>
      </div>

      <div class="form-field" *ngIf="measureFieldMode === 'single'">
        <label>Impacted Measure <span class="required">*</span></label>
        <mat-form-field appearance="outline" class="measure-select-field" subscriptSizing="dynamic">
          <mat-select formControlName="impactedMeasureCode" placeholder="Measure Selection">
            <mat-option>
              <ngx-mat-select-search
                [formControl]="measureSearchControl"
                placeholderLabel="Search measures..."
                noEntriesFoundLabel="No measures found">
              </ngx-mat-select-search>
            </mat-option>
            <mat-option *ngFor="let option of filteredMeasureOptions" [value]="option.code">
              {{ option.title }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="info-message" *ngIf="measureFieldMode !== 'none' && impactedMeasureOptions.length === 0">
        No measures available — open Report Issue from the Dashboard to select a measure.
      </div>

      <div class="form-field" *ngIf="isBiReportsCategory">
        <label>Report</label>
        <mat-form-field appearance="outline" class="measure-select-field" subscriptSizing="dynamic">
          <mat-select formControlName="reportName" placeholder="Select report">
            <mat-option *ngFor="let report of availableReports" [value]="report.reportName">
              {{ report.reportName }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="form-field">
        <label>Organization <span class="required">*</span></label>
        <input type="text" [value]="getOrganizationDisplayValue()" readonly>
      </div>

      <div class="form-field">
        <label>Summary <span class="required">*</span></label>
        <input type="text" formControlName="summary">
        <div class="form-feedback">
          <div class="char-count">
            {{ getCharacterCount('summary') }}/{{ VALIDATION_LIMITS.SUMMARY_MAX }}
          </div>
        </div>
        <div class="error-message" *ngIf="hasFieldError('summary', 'required')">
          Summary is required
        </div>
        <div class="error-message" *ngIf="hasFieldError('summary', 'maxlength')">
          Summary cannot exceed {{ VALIDATION_LIMITS.SUMMARY_MAX }} characters
        </div>
      </div>

      <div class="form-field">
        <label>Description <span class="required">*</span></label>
        <textarea formControlName="description" rows="4"></textarea>
        <div class="form-feedback">
          <div class="char-count">
            {{ getCharacterCount('description') }}/{{ VALIDATION_LIMITS.DESCRIPTION_MAX }}
          </div>
        </div>
        <div class="error-message" *ngIf="hasFieldError('description', 'required')">
          Description is required
        </div>
        <div class="error-message" *ngIf="hasFieldError('description', 'maxlength')">
          Description cannot exceed {{ VALIDATION_LIMITS.DESCRIPTION_MAX }} characters
        </div>
      </div>

      <div class="checkbox-container">
        <label class="checkbox-label">
          <input type="checkbox" [(ngModel)]="includePhiSection" (change)="onPhiToggle()" [ngModelOptions]="{standalone: true}">
          <span class="checkmark"></span>
          Attachment Contains PHI
        </label>
      </div>

      <div class="file-upload-section">
        <label>Attachments</label>

        <div class="upload-area" 
             [class.drag-over]="isDragOver"
             (click)="fileInput.click()"
             (dragover)="onDragOver($event)"
             (dragleave)="onDragLeave($event)"
             (drop)="onDrop($event)">
          
          <div class="upload-content">
            <span class="upload-text">
              {{ attachments.length > 0 ? 'Add more files' : 'Click to upload or drag files here' }}
            </span>
            <small class="upload-hint">
              Images, PDFs, Word docs, HTML, XML, Text files (Max: 25MB each)
            </small>
          </div>
          
          <input #fileInput
                 type="file"
                 multiple
                 [accept]="FILE_ACCEPT"
                 (change)="onFileSelected($event)"
                 style="display: none;">
        </div>

        <div class="attachment-list" *ngIf="attachments.length > 0">
          <div class="attachment-item" *ngFor="let attachment of attachments; let i = index">
            <div class="file-info">
              <span class="file-name">{{ attachment.name }}</span>
              <span class="file-size">{{ formatFileSize(attachment.size) }}</span>
            </div>
            <div class="attachment-doc-fields">
              <div class="form-field attachment-doc-type">
                <label>Document Type <span class="required">*</span></label>
                <select [(ngModel)]="attachment.dataEnrichmentDocumentType" [ngModelOptions]="{standalone: true}" required>
                  <option value="">Select type</option>
                  <option *ngFor="let type of documentTypes" [value]="type">{{ type }}</option>
                </select>
              </div>
              <div class="form-field attachment-doc-type-other" *ngIf="attachment.dataEnrichmentDocumentType === 'Other'">
                <label>Specify document type <span class="required">*</span></label>
                <input type="text" [(ngModel)]="attachment.dataEnrichmentDocumentTypeOther" [ngModelOptions]="{standalone: true}" placeholder="Enter document type">
              </div>
              <div class="form-field attachment-doc-date">
                <label>Document Date <span class="required">*</span></label>
                <input type="date" [(ngModel)]="attachment.dataEnrichmentDocumentDate" [ngModelOptions]="{standalone: true}" required>
              </div>
            </div>
            <button type="button" class="remove-attachment-btn" (click)="removeFile(i)">×</button>
          </div>
        </div>

        <div class="file-errors" *ngIf="fileErrors.length > 0">
          <div class="error-message" *ngFor="let error of fileErrors">
            {{ error }}
          </div>
        </div>
      </div>

      <div class="form-field" *ngIf="includePhiSection">
        <label>PHI Description <span class="required">*</span></label>
        <textarea formControlName="phiDescription" rows="4"></textarea>
        <div class="form-feedback">
          <div class="char-count">
            {{ getCharacterCount('phiDescription') }}/{{ VALIDATION_LIMITS.PHI_DESCRIPTION_MAX }}
          </div>
        </div>
        <div class="error-message" *ngIf="hasFieldError('phiDescription', 'required')">
          PHI Description is required when "Attachment Contains PHI" is selected
        </div>
        <div class="error-message" *ngIf="hasFieldError('phiDescription', 'maxlength')">
          PHI Description cannot exceed {{ VALIDATION_LIMITS.PHI_DESCRIPTION_MAX }} characters
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-cancel" (click)="onCancel()">CANCEL</button>
        <button type="submit" class="btn btn-submit" [disabled]="!isFormValid">SUBMIT</button>
      </div>
    </form>
  </div>
</div>